import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertTypeEnum } from '@notifications/constants/constants';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { AlertIdData, Notification } from '@usf/alert-types';
import { NotificationStoreService } from '@usf/ngrx-alerts';
import {
  of,
  firstValueFrom,
  Observable,
  switchMap,
  filter,
  map,
  timeout,
  Subject,
  combineLatest,
  EMPTY,
} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  first,
  pairwise,
  take,
  tap,
} from 'rxjs/operators';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import { NotificationsService } from '../../services/notifications.service';
import { TranslateService } from '@ngx-translate/core';
import { LoginService } from '@app/login/services';
import { PATHS } from '@shared/constants/paths';
import { CustomerStoreService } from '../../../ngrx-customer/services/customer/customer-store.service';
import { ActionsSubject } from '@ngrx/store';
import { ofType } from '@ngrx/effects';
import { ALERTS_ACTIONS } from '@usf/ngrx-alerts';

export interface NotificationMetadata {
  messageTitle: string;
  linkLabel?: string;
  orderNumber?: string;
}

interface NotificationDetailsPageViewModel {
  notification: Notification;
  notificationMetadata: NotificationMetadata;
}

const QueryStringParams = {
  ORIGIN_TYPE: 'originType',
  ORDER_INFO: 'orderInfo',
  IDENTIFIER: 'notificationId',
} as const;

@Component({
  selector: 'app-notification-details',
  templateUrl: './notification-details.page.html',
  styleUrls: ['./notification-details.page.scss'],
})
export class NotificationDetailsPage implements OnInit {
  // Array of AlertTypeEnum values that should trigger customer switching
  private static readonly CUSTOMER_SWITCH_ALERT_TYPES: AlertTypeEnum[] = [
    AlertTypeEnum.ORDERED_PRODUCT_UNAVAILABLE,
  ];

  private static readonly NOTIFICATION_TIMEOUT_MS: number = 10000;

  public notificationId: string;

  public breadcrumbs: Crumb[];
  public isDesktop: boolean;
  public originType: string;
  public orderInfo: string;
  public vm$: Observable<NotificationDetailsPageViewModel>;

  private notificationIdSubject: Subject<string | undefined> =
    new Subject<string>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly panAppState: PanAppState,
    private readonly platformService: PlatformService,
    private readonly navigationHelperService: NavigationHelperService,
    private readonly notificationService: NotificationsService,
    private readonly notificationStoreService: NotificationStoreService,
    private readonly translateService: TranslateService,
    private readonly loginService: LoginService,
    private readonly customerStoreService: CustomerStoreService,
    private readonly actionSubject: ActionsSubject, // to be removed
  ) {
    this.panAppState.setPageTitle('Details');
    this.isDesktop =
      this.platformService.platformType ===
      this.platformService.platformEnum.desktop;
  }

  public ngOnInit() {
    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: '/desktop/home',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.notifications',
        url: '/notifications',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.notificationDetails',
        url: '',
        active: true,
      },
    ];

    this.vm$ = this.notificationIdSubject.pipe(
      this.fetchNotification(),
      this.handleCustomerSwitching(),
      this.trackAnalytics(),
      this.buildNotificationMetadata(),
    );
  }

  ionViewDidEnter(): void {
    // Extract the origin type and order info from the route query params
    this.originType =
      this.route.snapshot.queryParams[QueryStringParams.ORIGIN_TYPE];
    this.orderInfo =
      this.route.snapshot.queryParams[QueryStringParams.ORDER_INFO];

    // Extract the notification identifier from the route params
    const identifier = this.route.snapshot.params[QueryStringParams.IDENTIFIER];

    // If no notification identifier is found, navigate to the home page
    if (!identifier) {
      console.error('No notification identifier found in route params');
      this.navigationHelperService.routeTo(PATHS.HOME);
      return;
    }

    // Log the attempt to extract the notification ID
    console.log('Attempting to extract notification ID from:', {
      originType: this.originType,
      identifier,
    });

    // Extract the notification ID from the given origin type and identifier
    this.extractNotificationId(this.originType, identifier)
      .then((notificationId: string) => {
        if (notificationId) {
          console.log(
            '***** Successfully extracted notification ID:',
            notificationId,
          );
          this.notificationId = notificationId; // Store the ID property
          this.notificationIdSubject.next(notificationId);
        } else {
          console.error('Failed to extract notification ID');
          this.navigationHelperService.routeTo(PATHS.HOME);
        }
      })
      .catch(error => {
        console.error('Error extracting notification ID:', error);
        // handle notification not found
        this.navigationHelperService.routeTo(PATHS.HOME);
      });
  }

  /**
   * Fetches notification based on notification ID and handles error cases
   */
  private fetchNotification() {
    return (
      source: Observable<string | undefined>,
    ): Observable<Notification | undefined> =>
      source.pipe(
        filter(notificationId => !!notificationId),
        switchMap((notificationId: string) =>
          this.notificationStoreService
            .getNotificationWithId(notificationId)
            .pipe(
              // only way to determine not found ATM
              timeout(NotificationDetailsPage.NOTIFICATION_TIMEOUT_MS),
              tap(notification => {
                if (!notification) {
                  // alerts must not be loaded into state
                  // trigger them to be loaded (unfortunate)
                  firstValueFrom(
                    this.actionSubject?.pipe(
                      ofType(ALERTS_ACTIONS.getAlertsSuccess),
                    ),
                  );
                }
              }),
              filter(notification => !!notification),
              distinctUntilChanged((prev: Notification, curr: Notification) => {
                return prev.alertBatchId === curr.alertBatchId;
              }),
              take(1),
              catchError(err => {
                console.error(err);
                this.handleNavigationOnError();
                return EMPTY;
              }),
            ),
        ),
        map((notification: Notification) => {
          if (notification) {
            console.log('Found notification:', notification);
            this.notificationService.markAsRead(notification.alertBatchId);
            return notification;
          }
        }),
      );
  }

  /**
   * Handles customer switching logic when required based on notification type
   */
  private handleCustomerSwitching() {
    return (source: Observable<Notification>): Observable<Notification> =>
      source.pipe(
        switchMap((notification: Notification) => {
          if (!notification) {
            return EMPTY;
          } else if (
            !NotificationDetailsPage.CUSTOMER_SWITCH_ALERT_TYPES.includes(
              notification?.alertId as AlertTypeEnum,
            )
          ) {
            return of({ notification });
          } else {
            return combineLatest([
              of(notification),
              this.panAppState.customer$.pipe(
                filter(customer => !!customer),
                map(customer => customer?.customerNumber ?? undefined),
              ),
            ]).pipe(
              first(),
              map(([notification, customerNumber]) => ({
                notification,
                customerNumber,
              })),
            );
          }
        }),
        switchMap(
          (switchDetail: {
            notification: Notification;
            customerNumber?: number;
          }) => {
            // for whatever reason customerNumber
            // is not always a number at runtime
            if (
              !!switchDetail.customerNumber &&
              String(switchDetail.customerNumber) !==
                String(switchDetail.notification.customerNumber)
            ) {
              this.doCustomerSwitch(switchDetail.notification);
              return combineLatest([
                of(switchDetail.notification),
                this.customerStoreService.customerSwitchStatus$().pipe(
                  pairwise(),
                  tap(([_previous, current]) => {
                    if (current === 'failed') {
                      throw new Error('Customer switch failed');
                    }
                  }),
                  filter(
                    ([previous, current]) =>
                      previous === 'pending' && current === 'complete',
                  ),
                  map(() => true),
                  catchError(err => {
                    console.error(err);
                    return of(false);
                  }),
                ),
              ]).pipe(
                first(),
                map(([notification]) => notification),
              );
            } else {
              return of(switchDetail.notification);
            }
          },
        ),
      );
  }

  /**
   * Tracks analytics events based on notification type
   */
  private trackAnalytics() {
    return (source: Observable<Notification>): Observable<Notification> =>
      source.pipe(
        map(notification => {
          // analytics
          if (notification?.alertId === AlertTypeEnum.DELIVERY_EXCEPTION) {
            this.notificationService.trackAlertDetailsPageLoad(
              notification?.alertBatchId,
            );
          } else if (
            notification?.alertId === AlertTypeEnum.ORDERED_PRODUCT_UNAVAILABLE
          ) {
            this.notificationService.trackOrderedProductsUnavailablePageLoad();
          }
          return notification;
        }),
      );
  }

  /**
   * Builds notification metadata based on notification type
   */
  private buildNotificationMetadata() {
    return (
      source: Observable<Notification>,
    ): Observable<NotificationDetailsPageViewModel> =>
      source.pipe(
        map(notification => {
          if (notification?.alertId === AlertTypeEnum.DELIVERY_EXCEPTION) {
            return {
              notification,
              notificationMetadata: {
                messageTitle: this.translateService.instant(
                  'i18n.notifications.details',
                ),
                linkLabel: this.translateService.instant(
                  AlertIdData.find(a => a.alertId === notification.alertId)
                    .detailLinkLabel,
                ),
                orderNumber: notification.alertContext?.find(
                  x => x.key === 'orderNumber',
                )?.value,
              },
            };
          } else if (
            notification?.alertId === AlertTypeEnum.ORDERED_PRODUCT_UNAVAILABLE
          ) {
            return {
              notification,
              notificationMetadata: {
                messageTitle: this.translateService.instant(
                  'i18n.notifications.ctaLinkLabels.orderedProductUnavailable',
                ),
              },
            };
          } else {
            return {
              notification,
              notificationMetadata: {
                messageTitle: notification?.messageTitle,
              },
            };
          }
        }),
      );
  }

  /**
   * Extracts the notification ID from the given origin type and identifier
   * @param originType The origin type of the notification
   * @param identifier The identifier of the notification
   * @returns A promise that resolves to the notification ID
   */
  private extractNotificationId(
    originType: string,
    identifier: string, // either alertBatchId or notificationId
  ): Promise<string> {
    if (
      originType === 'email-alerts-whscuts' ||
      originType === 'push-alerts-whscutsOrderUnavailable'
    ) {
      return firstValueFrom(
        this.notificationService
          .getAlertBatchIdByDetailsId(identifier)
          .pipe(filter(alertBatchId => !!alertBatchId)),
      );
    } else {
      return Promise.resolve(identifier);
    }
  }

  private async doCustomerSwitch(notification: Notification): Promise<void> {
    // Check if switchToCustomer returns an Observable or Promise
    const switchResult = this.loginService.switchToCustomer(
      notification?.customerNumber,
      notification?.departmentNumber ?? 0,
      notification?.divisionNumber,
    );
    // Handle the result appropriately
    if (switchResult instanceof Promise) {
      await switchResult;
    } else {
      // It's an Observable, wait for it to complete
      await firstValueFrom(switchResult);
    }
  }

  /**
   * Handles navigation when a notification is not found or retrieval fails
   * Routes to appropriate page based on originType and orderInfo
   */
  private handleNavigationOnError(): void {
    if (this.originType === 'push-alerts-deliveryIssues') {
      if (this.orderInfo) {
        this.navigationHelperService.routeToSubmittedOrder(
          this.orderInfo,
          true,
        );
      } else {
        this.navigationHelperService.routeTo(PATHS.MY_ORDERS);
      }
    } else {
      this.navigationHelperService.routeTo(PATHS.HOME);
    }
  }
}
